import { create } from "zustand";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import { toast } from "react-hot-toast";
import useAuthStore from "../useAuthStore";

const useServiceAreaStore = create((set, get) => ({
  builderServiceAreas: [],
  allServiceAreas: [],
  pendingExpansionRequests: [],
  totalServiceAreaCost: 0,
  globalGstSettings: null,
  isLoading: false,
  isSubmitting: false,
  error: null,
  customOffer: null,
  hasCustomOffer: false,

  fetchBuilderServiceAreas: async () => {
    set({ isLoading: true });

    const { token, user } = useAuthStore.getState();

    // LIVE FIX : extract correct builder ID
    const builderId =
      user?.builder_profile_id ||
      user?.builderId ||
      user?.id ||
      null;

    if (!token || !builderId) {
      set({ isLoading: false, error: "Authentication required." });
      return;
    }

    try {
      // SERVICE AREA DETAILS
      const response1 = await axiosInstancev1.get(
        `/builder/${builderId}/service-area-details`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-builder-id": builderId, // 🔥 LIVE FIX
          },
        }
      );

      // CUSTOM SUBSCRIPTION OFFER
      const response2 = await axiosInstancev1.get(
        `/subscriptions/builder/offer`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-builder-id": builderId, // 🔥 LIVE FIX
          },
        }
      );

      if (response1.data.success) {
        set({
          builderServiceAreas: response1.data.activeServiceAreas || [],
          totalServiceAreaCost: response1.data.totalServiceAreaCost || 0,
          globalGstSettings: response1.data.globalGstSettings || null,
        });
      }

      if (response2.data.success && response2.data.hasOffer) {
        set({
          customOffer: response2.data.offer,
          hasCustomOffer: true,
        });
      }

    } catch (error) {
      toast.error("Failed to fetch service areas");
      set({ error: error.message });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchAllServiceAreas: async () => {
    set({ isLoading: true });

    const { token } = useAuthStore.getState();

    if (!token) {
      set({ isLoading: false, error: "Authentication required." });
      return;
    }

    try {
      const response = await axiosInstancev1.get(
        `/address/service-area-options`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        set({
          allServiceAreas: response.data.data || [],
        });
      }
    } catch (error) {
      toast.error("Failed to fetch all service areas");
      set({ error: error.message, allServiceAreas: [] });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchPendingExpansionRequests: async () => {
    const { token, user } = useAuthStore.getState();

    const builderId =
      user?.builder_profile_id ||
      user?.builderId ||
      user?.id ||
      null;

    if (!token || !builderId) {
      return;
    }

    try {
      const response = await axiosInstancev1.get(
        `/builder/pending-expansion-requests`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-builder-id": builderId,
          },
        }
      );

      if (response.data.success) {
        set({
          pendingExpansionRequests: response.data.data || [],
        });
      }
    } catch (error) {
      console.error("Failed to fetch pending expansion requests:", error);
      set({ pendingExpansionRequests: [] });
    }
  },

  requestServiceAreaExpansion: async (talukIds) => {
    if (!talukIds.length) {
      toast.error("Select at least one taluk.");
      return { success: false };
    }

    const { token, user } = useAuthStore.getState();

    const builderId =
      user?.builder_profile_id ||
      user?.builderId ||
      user?.id ||
      null;

    if (!token || !builderId) {
      toast.error("Authentication required.");
      return { success: false };
    }

    set({ isSubmitting: true });

    try {
      const response = await axiosInstancev1.post(
        `/builder/service-area-expansion-request`,
        { selectedTalukIds: talukIds },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-builder-id": builderId, // 🔥 LIVE FIX
          },
        }
      );

      if (response.data.success) {
        toast.success("Expansion request submitted!");
        return { success: true };
      }

    } catch (error) {
      toast.error("Failed to send request.");
    } finally {
      set({ isSubmitting: false });
    }

    return { success: false };
  },
}));

export default useServiceAreaStore;
